import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaBalanceScale,
  FaBell,
  FaBox,
  FaCheckCircle,
  FaChevronDown,
  FaFileAlt,
  FaFilter,
  FaHeart,
  FaInfoCircle,
  FaList,
  FaRegTrashAlt,
  FaShareAlt,
  FaShieldAlt,
  FaShoppingCart,
} from "react-icons/fa";
import { toast } from "react-toastify";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import HubFloatingRail from "../../components/hub/HubFloatingRail";
import MarketplaceRelatedProductsSection from "../../components/marketplace/MarketplaceRelatedProductsSection";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  addMarketplaceCartItem,
  fetchMarketplaceCart,
  fetchMarketplaceWishlist,
  removeMarketplaceWishlistItem,
  saveMarketplaceCartAsList,
} from "../../services/marketplaceCart.service";
import StorageService from "../../services/StorageService";
import { getViewProfileRouteByRole } from "../../utils/roleSwitcher";
import { formatCartInr, normalizeMarketplaceCart } from "../../utils/marketplaceCart.util";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/my_wishlist.scss";

const EMPTY_WISHLIST = { totalItems: 0, suppliers: [] };

const WISHLIST_FEATURES = [
  { id: "alerts", title: "Price Alerts", description: "Get notified when price drops", icon: FaBell },
  { id: "bulk", title: "Bulk Order", description: "Add multiple items to cart", icon: FaBox },
  { id: "compare", title: "Compare Products", description: "Compare similar products", icon: FaBalanceScale },
  { id: "secure", title: "Secure & Trusted", description: "100% secure transactions", icon: FaShieldAlt },
];

function ProductThumb({ name, imageUrl }) {
  if (imageUrl) {
    return (
      <div className="mywishlist-item__thumb">
        <img src={resolvePublicFileUrl(imageUrl)} alt={name} />
      </div>
    );
  }

  return (
    <div className="mywishlist-item__thumb" aria-hidden>
      <FaBox />
      <span>{name.split(" ").slice(0, 2).join(" ")}</span>
    </div>
  );
}

function sortWishlistSuppliers(suppliers, sortBy) {
  const next = suppliers.map((supplier) => ({
    ...supplier,
    items: [...(supplier.items || [])],
  }));

  next.forEach((supplier) => {
    supplier.items.sort((a, b) => {
      if (sortBy === "name") return String(a.name).localeCompare(String(b.name));
      if (sortBy === "price") return (Number(a.unitPrice) || 0) - (Number(b.unitPrice) || 0);
      return Number(b.id) - Number(a.id);
    });
  });

  if (sortBy === "name") {
    next.sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }

  return next;
}

export default function MyWishlistPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const moveMenuRef = useRef(null);
  const [wishlist, setWishlist] = useState(EMPTY_WISHLIST);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sortBy, setSortBy] = useState("recent");
  const [stockFilter, setStockFilter] = useState("all");
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const [saveListOpen, setSaveListOpen] = useState(false);
  const [saveListName, setSaveListName] = useState("");

  const loadWishlist = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMarketplaceWishlist();
      const normalized = normalizeMarketplaceCart(data);
      setWishlist(normalized || EMPTY_WISHLIST);
      setSelectedIds(new Set());
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Could not load wishlist.";
      toast.error(message);
      setWishlist(EMPTY_WISHLIST);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!StorageService.getToken()) {
      navigate("/login", { state: { from: location.pathname } });
      return undefined;
    }
    void loadWishlist();
    return undefined;
  }, [loadWishlist, location.pathname, navigate]);

  useEffect(() => {
    if (!moveMenuOpen) return undefined;
    const onDocClick = (event) => {
      if (moveMenuRef.current?.contains(event.target)) return;
      setMoveMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [moveMenuOpen]);

  const allItems = useMemo(
    () => wishlist.suppliers.flatMap((supplier) => supplier.items || []),
    [wishlist.suppliers]
  );

  const visibleSuppliers = useMemo(() => {
    let suppliers = sortWishlistSuppliers(wishlist.suppliers, sortBy);
    if (stockFilter === "in-stock") {
      suppliers = suppliers
        .map((supplier) => ({
          ...supplier,
          items: (supplier.items || []).filter((item) => item.inStock),
        }))
        .filter((supplier) => (supplier.items || []).length > 0);
    }
    return suppliers;
  }, [wishlist.suppliers, sortBy, stockFilter]);

  const visibleItems = useMemo(
    () => visibleSuppliers.flatMap((supplier) => supplier.items || []),
    [visibleSuppliers]
  );

  const totalItems = wishlist.totalItems || allItems.length;
  const selectedItems = useMemo(
    () => allItems.filter((item) => selectedIds.has(item.id)),
    [allItems, selectedIds]
  );
  const allVisibleSelected =
    visibleItems.length > 0 && visibleItems.every((item) => selectedIds.has(item.id));

  const runMutation = async (fn) => {
    if (saving) return null;
    setSaving(true);
    try {
      return await fn();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Action failed.";
      toast.error(message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = (itemId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(visibleItems.map((item) => item.id)));
  };

  const handleRemove = async (item) => {
    const data = await runMutation(() => removeMarketplaceWishlistItem(item.productId));
    if (data) {
      const normalized = normalizeMarketplaceCart(data);
      setWishlist(normalized || EMPTY_WISHLIST);
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });
      toast.info("Removed from wishlist.");
    }
  };

  const handleAddToCart = async (items) => {
    const list = Array.isArray(items) ? items : [items];
    if (!list.length) {
      toast.warning("Select at least one item.");
      return;
    }
    setSaving(true);
    try {
      for (const item of list) {
        await addMarketplaceCartItem(item.productId, item.quantity || 1);
      }
      toast.success(`${list.length} item${list.length === 1 ? "" : "s"} added to cart.`);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Could not add items to cart.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleShareWishlist = async () => {
    const shareUrl = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "My Wishlist",
          text: "View my Trocanet wishlist",
          url: shareUrl,
        });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Wishlist link copied.");
    } catch {
      toast.info("Could not share wishlist.");
    }
  };

  const handleRequestQuote = async (items) => {
    const list = Array.isArray(items) ? items : [items];
    if (!list.length) {
      toast.warning("Select at least one item to request a quote.");
      return;
    }
    setSaving(true);
    try {
      for (const item of list) {
        await addMarketplaceCartItem(item.productId, item.quantity || 1);
      }
      toast.success("Items added to cart. Open My Cart to generate RFQ.");
      navigate("/my-cart");
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Could not prepare quote request.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openMoveToList = () => {
    if (!selectedItems.length) {
      toast.warning("Select at least one item to move.");
      return;
    }
    setMoveMenuOpen(false);
    setSaveListName("");
    setSaveListOpen(true);
  };

  const handleSaveToList = async () => {
    const listName = saveListName.trim();
    if (!listName) {
      toast.warning("Enter a list name.");
      return;
    }
    if (!selectedItems.length) {
      toast.warning("Select at least one item.");
      return;
    }

    setSaving(true);
    try {
      for (const item of selectedItems) {
        await addMarketplaceCartItem(item.productId, item.quantity || 1);
      }

      const cart = await fetchMarketplaceCart();
      const productIdSet = new Set(selectedItems.map((item) => item.productId));
      const itemIds = (cart?.suppliers || [])
        .flatMap((supplier) => supplier.items || [])
        .filter((item) => productIdSet.has(item.productId))
        .map((item) => item.id)
        .filter(Boolean);

      await saveMarketplaceCartAsList(listName, { itemIds, onlySelected: false });
      toast.success(`Saved ${selectedItems.length} item(s) to "${listName}".`);
      setSaveListOpen(false);
      setSaveListName("");
      navigate("/my-saved-lists");
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Could not save list.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const addAllLabel = selectedItems.length || totalItems;

  return (
    <div className="mywishlist-page">
      <B2BHubHomeHeader />

      <main className="mywishlist-main">
        <nav className="mywishlist-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>›</span>
          <Link to={getViewProfileRouteByRole()}>My Account</Link>
          <span aria-hidden>›</span>
          <span aria-current="page">My Wishlist</span>
        </nav>

        <div className="mywishlist-hero">
          <div className="mywishlist-hero__left">
            <span className="mywishlist-hero__icon" aria-hidden>
              <FaHeart />
            </span>
            <div className="mywishlist-hero__title-wrap">
              <h1>My Wishlist ({totalItems} Items)</h1>
              <p className="mywishlist-hero__subtitle">All the products you have saved for later.</p>
            </div>
          </div>
          <div className="mywishlist-hero__actions">
            <button
              type="button"
              className="mywishlist-btn mywishlist-btn--outline"
              disabled={saving || !totalItems}
              onClick={handleShareWishlist}
            >
              <FaShareAlt aria-hidden /> Share Wishlist
            </button>
            <button
              type="button"
              className="mywishlist-btn mywishlist-btn--primary"
              disabled={saving || !allItems.length}
              onClick={() => handleAddToCart(selectedItems.length ? selectedItems : allItems)}
            >
              <FaShoppingCart aria-hidden /> Add All to Cart ({addAllLabel})
            </button>
          </div>
        </div>

        <nav className="mywishlist-tabs" aria-label="Shopping lists">
          <Link to="/my-cart" className="mywishlist-tabs__link">
            <FaShoppingCart aria-hidden /> My Cart
          </Link>
          <span className="mywishlist-tabs__link mywishlist-tabs__link--active" aria-current="page">
            <FaHeart aria-hidden /> My Wishlist
          </span>
          <Link to="/my-saved-lists" className="mywishlist-tabs__link">
            <FaList aria-hidden /> My Saved Lists
          </Link>
        </nav>

        {loading ? (
          <p className="mywishlist-status">Loading your wishlist…</p>
        ) : wishlist.suppliers.length === 0 ? (
          <div className="mywishlist-empty">
            <h2>Your wishlist is empty</h2>
            <p>Save products from your cart or browse the marketplace to build your wishlist.</p>
            <Link to="/marketplace/products" className="mywishlist-btn mywishlist-btn--primary">
              Browse Products
            </Link>
          </div>
        ) : (
          <>
            <div className="mywishlist-toolbar">
              <div className="mywishlist-toolbar__left">
                <label className="mywishlist-toolbar__select">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={saving || !visibleItems.length}
                    onChange={toggleSelectAll}
                  />
                  <span>Select All ({visibleItems.length})</span>
                </label>

                <div className="mywishlist-toolbar__dropdown-wrap" ref={moveMenuRef}>
                  <button
                    type="button"
                    className="mywishlist-toolbar__dropdown-btn"
                    disabled={saving}
                    onClick={() => setMoveMenuOpen((open) => !open)}
                  >
                    Move to List
                    <FaChevronDown aria-hidden />
                  </button>
                  {moveMenuOpen ? (
                    <div className="mywishlist-toolbar__dropdown-menu">
                      <button type="button" disabled={saving} onClick={openMoveToList}>
                        Save selected to new list
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="mywishlist-toolbar__right">
                <label className="mywishlist-toolbar__sort">
                  Sort by:
                  <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} disabled={saving}>
                    <option value="recent">Recently Added</option>
                    <option value="name">Name</option>
                    <option value="price">Price</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="mywishlist-toolbar__filter"
                  disabled={saving}
                  onClick={() =>
                    setStockFilter((current) => (current === "all" ? "in-stock" : "all"))
                  }
                >
                  <FaFilter aria-hidden />
                  {stockFilter === "in-stock" ? "In Stock" : "Filter"}
                </button>
              </div>
            </div>

            <section className="mywishlist-list" aria-label="Wishlist items">
              {visibleSuppliers.length === 0 ? (
                <div className="mywishlist-empty">
                  <h2>No items match your filter</h2>
                  <p>Try changing the filter to see more saved products.</p>
                </div>
              ) : (
                visibleSuppliers.map((supplier) => (
                  <article key={supplier.id} className="mywishlist-supplier">
                    <header className="mywishlist-supplier__head">
                      <div className="mywishlist-supplier__meta">
                        <span className="mywishlist-supplier__label">Supplier:</span>
                        <strong>{supplier.name}</strong>
                        {supplier.verified ? (
                          <span className="mywishlist-supplier__verified">
                            <FaCheckCircle aria-hidden /> Verified Supplier
                          </span>
                        ) : null}
                        <span className="mywishlist-supplier__gstin">GSTIN: {supplier.gstin}</span>
                      </div>
                      <span className="mywishlist-supplier__delivery">
                        Est. Delivery: {supplier.estDelivery}
                        <FaInfoCircle aria-hidden style={{ marginLeft: 6 }} />
                      </span>
                    </header>

                    {(supplier.items || []).map((item) => {
                      const lineTotal = item.unitPrice * item.quantity;
                      return (
                        <div key={item.id} className="mywishlist-item">
                          <label className="mywishlist-item__check">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(item.id)}
                              disabled={saving}
                              onChange={() => toggleItem(item.id)}
                            />
                          </label>

                          <Link
                            to={`/marketplace/products/${item.productId}`}
                            className="mywishlist-item__thumb-link"
                            aria-label={`View ${item.name}`}
                          >
                            <ProductThumb name={item.name} imageUrl={item.imageUrl} />
                          </Link>

                          <div className="mywishlist-item__body">
                            <h2 className="mywishlist-item__title">
                              <Link to={`/marketplace/products/${item.productId}`}>{item.name}</Link>
                            </h2>
                            <p className="mywishlist-item__sku">SKU: {item.sku}</p>
                            <div className="mywishlist-item__badges">
                              <span
                                className={`mywishlist-item__stock${
                                  item.inStock ? " mywishlist-item__stock--in" : " mywishlist-item__stock--out"
                                }`}
                              >
                                {item.inStock ? "In Stock" : "Out of Stock"}
                              </span>
                              <span>MOQ: {item.moq}</span>
                              <span>Lead Time: {item.leadTime}</span>
                              <span>Qty: {item.quantity}</span>
                            </div>
                          </div>

                          <div className="mywishlist-item__price-col">
                            <span className="mywishlist-item__unit">
                              {formatCartInr(item.unitPrice)} / {item.unit}
                            </span>
                            <strong className="mywishlist-item__total">{formatCartInr(lineTotal)}</strong>
                            <span className="mywishlist-item__save">You save {formatCartInr(0)}</span>
                          </div>

                          <div className="mywishlist-item__actions">
                            <button
                              type="button"
                              className="mywishlist-item__action mywishlist-item__action--cart"
                              disabled={saving}
                              onClick={() => handleAddToCart(item)}
                            >
                              <FaShoppingCart aria-hidden /> Add to Cart
                            </button>
                            <button
                              type="button"
                              className="mywishlist-item__action mywishlist-item__action--quote"
                              disabled={saving}
                              onClick={() => handleRequestQuote(item)}
                            >
                              <FaFileAlt aria-hidden /> Request Quote
                            </button>
                            <button
                              type="button"
                              className="mywishlist-item__action mywishlist-item__action--remove"
                              disabled={saving}
                              onClick={() => handleRemove(item)}
                            >
                              <FaRegTrashAlt aria-hidden /> Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </article>
                ))
              )}
            </section>

            <section className="mywishlist-features" aria-label="Wishlist features">
              {WISHLIST_FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.id} className="mywishlist-features__item">
                    <span className="mywishlist-features__icon" aria-hidden>
                      <Icon />
                    </span>
                    <div>
                      <p className="mywishlist-features__title">{feature.title}</p>
                      <p className="mywishlist-features__desc">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </section>
          </>
        )}

        {!loading ? (
          <MarketplaceRelatedProductsSection
            excludeProductIds={allItems.map((item) => item.productId)}
            saving={saving}
          />
        ) : null}
      </main>

      {saveListOpen ? (
        <div className="mywishlist-modal-backdrop" role="presentation" onClick={() => !saving && setSaveListOpen(false)}>
          <div
            className="mywishlist-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wishlist-save-list-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="wishlist-save-list-title">Save to list</h3>
            <p>Move {selectedItems.length} selected item(s) to a new saved list.</p>
            <input
              type="text"
              value={saveListName}
              onChange={(event) => setSaveListName(event.target.value)}
              placeholder="List name"
              maxLength={120}
              autoFocus
            />
            <div className="mywishlist-modal__actions">
              <button
                type="button"
                className="mywishlist-btn mywishlist-btn--outline"
                disabled={saving}
                onClick={() => setSaveListOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mywishlist-btn mywishlist-btn--primary"
                disabled={saving}
                onClick={handleSaveToList}
              >
                {saving ? "Saving…" : "Save List"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <HubHomeFooter />
      <HubFloatingRail />
    </div>
  );
}
